import React, { useState } from 'react';
import { Modal, Upload, Button, message, Alert } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import api from '../utils/axios';

interface UserImportProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const UserImport: React.FC<UserImportProps> = ({ visible, onClose, onSuccess }) => {
  const [file, setFile] = useState<RcFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleUpload = async () => {
    if (!file) {
      message.error('Please select a CSV file to upload.');
      return;
    }
    setUploading(true);
    setError(null);
    setSuccess(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post('/users/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        setSuccess(response.data.message || 'Users imported successfully!');
        message.success('Users imported successfully!');
        setFile(null);
        onSuccess();
      } else {
        setError(response.data.message || 'Import failed.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Import failed.');
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = (file: RcFile) => {
    setFile(file);
    setError(null);
    setSuccess(null);
    return false; // Prevent auto upload
  };

  const handleRemove = () => {
    setFile(null);
    setError(null);
    setSuccess(null);
  };

  return (
    <Modal
      title="Import Users from CSV"
      visible={visible}
      onCancel={onClose}
      onOk={handleUpload}
      okText="Import"
      confirmLoading={uploading}
      okButtonProps={{ disabled: !file || uploading }}
      destroyOnClose
    >
      <Upload
        beforeUpload={beforeUpload}
        onRemove={handleRemove}
        fileList={file ? [file] : []}
        accept=".csv"
        maxCount={1}
      >
        <Button icon={<UploadOutlined />}>Select CSV File</Button>
      </Upload>
      {error && <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />}
      {success && <Alert type="success" message={success} showIcon style={{ marginTop: 16 }} />}
      <div style={{ marginTop: 16 }}>
        <b>CSV format:</b>
        <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
firstName,lastName,email,password,role,tenantId,tenantName
John,Doe,john.doe@example.com,Password123,ADMIN,99b61f23-3697-4db8-acef-df7dfa43ea0a,
Jane,Smith,jane.smith@example.com,Password123,USER,,Default Tenant
        </pre>
      </div>
    </Modal>
  );
};

export default UserImport; 